import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { mockVideos, videoCategories } from '@/mocks/videoData';
import { mockArticles } from '@/mocks/articleData';

const hotVideos = mockVideos.slice(0, 8);
const latestArticles = mockArticles.slice(0, 3);

const features = [
  {
    icon: 'ri-shield-check-line',
    title: '高清画质',
    desc: '支持1080P/4K超清画质，给您影院级的视觉享受，每一帧都清晰锐利。',
  },
  {
    icon: 'ri-flashlight-line',
    title: '极速播放',
    desc: '智能CDN加速技术，多节点分发，确保视频流畅播放不卡顿、不缓冲。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '多端适配',
    desc: '完美适配电脑、平板、手机等各类设备，随时随地畅享观影乐趣。',
  },
  {
    icon: 'ri-global-line',
    title: '海量资源',
    desc: '聚合八大热门视频平台内容，每日更新海量影视资源，想看什么都有。',
  },
];

const stats = [
  { value: '5000+', label: '影视资源' },
  { value: '8', label: '合作平台' },
  { value: '99.9%', label: '播放成功率' },
  { value: '100万+', label: '日活用户' },
];

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[680px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theater%20interior%20with%20glowing%20red%20velvet%20curtains%20and%20warm%20golden%20stage%20lights%2C%20dramatic%20atmosphere%2C%20luxurious%20cinema%20hall%2C%20professional%20photography%2C%20deep%20shadows%20and%20highlights%2C%20grand%20scale%20architecture&width=1920&height=900&seq=hero-bg-home-01&orientation=landscape"
            alt="青青草视频 - 高清免费在线观看平台"
            title="青青草视频 - 聚合香蕉视频、红桃视频、91视频、蜜桃视频等热门影视平台"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60"></div>
          <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              青青草视频
            </h1>
            <p className="text-base md:text-xl text-background-200 mb-3 max-w-2xl mx-auto leading-relaxed">
              聚合香蕉视频、红桃视频、91视频、蜜桃视频等热门影视平台
            </p>
            <p className="text-sm md:text-lg text-background-300 mb-8 max-w-xl mx-auto">
              高清免费在线观看 · 不缓冲流畅播放 · 一站式影视聚合体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-base font-medium transition-all whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-xl"></i>
                立即观看
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-background-50/15 hover:bg-background-50/25 text-background-50 border border-background-50/30 px-8 py-3 rounded-full text-base font-medium transition-all whitespace-nowrap"
              >
                <i className="ri-information-line text-xl"></i>
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading">{stat.value}</div>
                <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Hot Videos Section */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                <i className="ri-fire-line text-accent-500 mr-2"></i>
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500">精选全网最热视频，实时更新热门内容</p>
            </div>
            <Link
              to="/category/all"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {hotVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Category Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                <i className="ri-apps-2-line text-primary-500 mr-2"></i>
                合作平台
              </h2>
              <p className="text-sm text-foreground-500">聚合八大热门视频平台，一站畅享海量影视内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {videoCategories.filter(c => c.slug !== 'all').map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="group bg-background-50 rounded-lg p-5 md:p-6 text-center transition-all hover:scale-[1.03] no-underline"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg group-hover:bg-primary-500 transition-colors">
                    <i className={`${category.icon} text-primary-600 group-hover:text-background-50 text-2xl transition-colors`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1 whitespace-nowrap">{category.name}</h3>
                  <p className="text-xs text-foreground-400 line-clamp-2">{category.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              <i className="ri-star-line text-accent-500 mr-2"></i>
              为什么选择青青草视频
            </h2>
            <p className="text-sm text-foreground-500">四大核心优势，打造极致观影体验</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className={`${feature.icon} text-accent-600 text-xl md:text-2xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Articles Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  <i className="ri-article-line text-primary-500 mr-2"></i>
                  影视资讯
                </h2>
                <p className="text-sm text-foreground-500">最新影视动态、观影指南、行业分析</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-[1000px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              <i className="ri-question-answer-line text-accent-500 mr-2"></i>
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">关于青青草视频的常见疑问解答</p>
          </div>
          <div className="space-y-3">
            {[
              { q: '青青草视频是否需要注册才能观看？', a: '无需注册！青青草视频所有内容均可免费在线观看，无需任何注册或登录步骤，打开即可畅享高清影视内容。' },
              { q: '支持哪些设备观看？', a: '青青草视频完美适配电脑、平板、手机等各类设备。无论您使用Windows、Mac、iOS还是Android系统，都能获得流畅的观看体验。' },
              { q: '视频播放卡顿怎么办？', a: '青青草视频采用智能CDN加速和多源切换技术。如果遇到卡顿，系统会自动为您切换到最优播放源。您也可以尝试切换清晰度或刷新页面。' },
              { q: '内容多久更新一次？', a: '青青草视频聚合的各大平台内容实时同步更新，热门影视、综艺、动漫等内容每日都会更新，确保您看到最新的精彩内容。' },
              { q: '青青草视频是免费的吗？', a: '完全免费！青青草视频致力于为用户提供免费高清的影视观看服务，聚合香蕉视频、红桃视频、91视频、蜜桃视频等多个平台的优质内容，全程无需付费。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-foreground-900 font-medium text-sm md:text-base list-none">
                  <span>{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '青青草视频',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '青青草视频聚合香蕉视频、红桃视频、91视频、蜜桃视频等热门影视平台，提供高清免费在线观看、不缓冲播放体验。',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: typeof window !== 'undefined' ? `${window.location.origin}/category/all?q={search_term_string}` : '/category/all?q={search_term_string}',
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '青青草视频',
            url: typeof window !== 'undefined' ? window.location.origin : '',
            description: '聚合香蕉视频、红桃视频、91视频、蜜桃视频等热门影视平台，提供高清免费在线观看服务。',
            foundingDate: '2023',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: 'service@qingcao-video.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '青青草视频是否需要注册才能观看？', acceptedAnswer: { '@type': 'Answer', text: '无需注册！青青草视频所有内容均可免费在线观看。' } },
              { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '青青草视频完美适配电脑、平板、手机等各类设备。' } },
              { '@type': 'Question', name: '视频播放卡顿怎么办？', acceptedAnswer: { '@type': 'Answer', text: '青青草视频采用智能CDN加速和多源切换技术，自动切换最优播放源。' } },
              { '@type': 'Question', name: '内容多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '各大平台内容实时同步更新，每日都会更新精彩内容。' } },
              { '@type': 'Question', name: '青青草视频是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '完全免费！聚合多个平台优质内容，全程无需付费。' } },
            ],
          }),
        }}
      />
    </>
  );
}